import React, { useState, useEffect, useRef } from 'react';
import { X, Package, AlertTriangle, Check } from 'lucide-react';

interface PackageScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  packageNo: number;
  visiblePackageNo?: number;
  itemNo?: string;
  customerName?: string;
}

/**
 * Modal for confirming package scanning during batch picking.
 * Operator must scan the correct package label before proceeding to quantity entry.
 */
export const PackageScanModal: React.FC<PackageScanModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  packageNo,
  visiblePackageNo,
  itemNo,
  customerName
}) => {
  const [scannedValue, setScannedValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setScannedValue('');
      setError(null);
      setIsValid(false);
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Validate scanned value against PackageNo
  const validateScan = (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      setError(null);
      setIsValid(false);
      return;
    }

    // Compare with PackageNo (as string for flexibility)
    if (trimmedValue === packageNo.toString()) {
      setError(null);
      setIsValid(true);
    } else {
      setError('Feil pakkenummer! Skann riktig pakke.');
      setIsValid(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setScannedValue(value);
    validateScan(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isValid) {
        onConfirm();
      } else if (scannedValue.trim()) {
        // Trigger validation on Enter if not already valid
        validateScan(scannedValue);
      }
    }
  };

  const handleConfirm = () => {
    if (isValid) {
      onConfirm();
    }
  };

  if (!isOpen) return null;

  // Display number - prefer VisiblePackageNo if available, otherwise PackageNo
  const displayPackageNo = visiblePackageNo ?? packageNo;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Package className="text-orange-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Bekreft pakke</h2>
              <p className="text-sm text-gray-500">Skann pakkeetiketten</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Package number display - LARGE and prominent */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6 text-center">
            <p className="text-sm text-blue-600 font-medium mb-2">Legg varen i pakke:</p>
            <div className="text-6xl font-bold text-blue-800 mb-2">
              {displayPackageNo}
            </div>
            {visiblePackageNo && visiblePackageNo !== packageNo && (
              <p className="text-xs text-blue-500">
                (Intern pakke-ID: {packageNo})
              </p>
            )}
            {customerName && (
              <p className="text-sm text-blue-700 font-medium mt-2">
                {customerName}
              </p>
            )}
          </div>

          {/* Item info if available */}
          {itemNo && (
            <div className="text-sm text-gray-600 mb-4 text-center">
              Vare: <span className="font-medium">{itemNo}</span>
            </div>
          )}

          {/* Scan input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skann pakke-strekkode:
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={scannedValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={`w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none transition-colors ${
                  isValid
                    ? 'border-green-500 bg-green-50 focus:border-green-600'
                    : error
                      ? 'border-red-500 bg-red-50 focus:border-red-600'
                      : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="Skann eller skriv pakkenummer..."
                autoComplete="off"
                autoFocus
              />
              {isValid && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Check className="text-green-600" size={24} />
                </div>
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-red-600 flex-shrink-0" size={20} />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Success message */}
          {isValid && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <Check className="text-green-600 flex-shrink-0" size={20} />
                <p className="text-sm text-green-800 font-medium">
                  Riktig pakke! Trykk Enter eller Bekreft for fortsette.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className={`px-6 py-2 min-h-[44px] text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              isValid
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Check size={18} />
            Bekreft
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageScanModal;
